import React from 'react';
import { Button, IconButton } from '@x5-react-uikit/core';
import clsx from 'clsx';

import {
  Container,
  PagesWrapper,
  PagePlaceholder,
  useActiveStyle,
} from './styles';

const PAGE_LIMIT = 10;

interface Props {
  active?: number;
  className?: string;
  count: number;
  limit?: number;
  onPageChange: (page: number) => any;
}

const Pagination: React.FC<Props> = ({
  active = 1,
  count,
  limit = PAGE_LIMIT,
  onPageChange,
}) => {
  const { activeState } = useActiveStyle();

  const getPagesCount = () => Math.ceil(count / limit);

  const getPages = () => {
    const pagesCount = getPagesCount();
    const pages: number[] = [];

    const addPages = (
      list: number[],
      condition: (page: number) => boolean,
      prepend: boolean = false
    ) => {
      list.forEach((item) => {
        if (condition(item)) {
          prepend ? pages.unshift(item) : pages.push(item);
        }
      });
    };

    addPages([active - 1], (page) => page > 0);
    addPages([active, active + 1], (page) => page <= pagesCount);

    if (pages.length < 3) {
      addPages([active + 2], (page) => page <= pagesCount);
    }

    if (pages.length < 3) {
      addPages([active - 2], (page) => page > 0, true);
    }

    return pages;
  };

  const pagesCount = getPagesCount();
  const pages = getPages();

  if (pagesCount <= 1 || active > pagesCount) {
    return null;
  }

  return (
    <Container>
      {active > 1 && (
        <IconButton
          size="medium"
          variant="outlined"
          icon="ChevronLeft"
          onClick={() => onPageChange(active - 1)}
          // @ts-ignore
          style={{ backgroundColor: 'transparent' }}
        />
      )}

      <PagesWrapper>
        {active > 2 && pagesCount > 3 && (
          <>
            <Button
              size="medium"
              variant="text"
              onClick={() => onPageChange(1)}
            >
              <span style={{ minWidth: '24px' }}>1</span>
            </Button>

            {active > 3 && <PagePlaceholder>...</PagePlaceholder>}
          </>
        )}

        {pages.map((page) => (
          <Button
            className={clsx({ [activeState]: page === active })}
            size="medium"
            variant="text"
            key={page}
            onClick={() => onPageChange(page)}
          >
            <span style={{ minWidth: '24px' }}>{page}</span>
          </Button>
        ))}

        {pages[pages.length - 1] !== pagesCount && (
          <>
            {pagesCount - pages[pages.length - 1] > 1 && (
              <PagePlaceholder>...</PagePlaceholder>
            )}

            <Button
              size="medium"
              variant="text"
              onClick={() => onPageChange(pagesCount)}
            >
              <span style={{ minWidth: '24px' }}>{pagesCount}</span>
            </Button>
          </>
        )}
      </PagesWrapper>

      {active < pagesCount && (
        <IconButton
          size="medium"
          variant="outlined"
          icon="ChevronRight"
          onClick={() => onPageChange(active + 1)}
          // @ts-ignore
          style={{ backgroundColor: 'transparent' }}
        />
      )}
    </Container>
  );
};

export default React.memo(Pagination);
